import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Load a page's data when its inputs change, dropping answers to questions
 * nobody is asking any more (a period switched mid-flight). `reload` asks
 * again without clearing what is on screen.
 */
export function useLoader<T>(load: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const run = useRef(0);

  const execute = useCallback(
    async (quiet: boolean) => {
      const id = ++run.current;
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError(false);
      try {
        const result = await load();
        if (id === run.current) setData(result);
      } catch (e) {
        console.error(e);
        if (id === run.current) setError(true);
      } finally {
        if (id === run.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  useEffect(() => {
    execute(false);
  }, [execute]);

  return { data, loading, refreshing, error, reload: () => execute(true) };
}
